import React, { createContext, useContext, useReducer, useEffect, ReactNode, useState, useMemo, useCallback } from 'react';
import { CartItem, Product, Accessory } from '../types';
import { trackAddToCart } from '../utils/metaPixel';
import { getEffectivePrice } from '../utils/pricing';

// Generate unique cart item ID from product + color + accessories
const generateCartItemId = (productId: string, colorIndex?: number, accessories?: Accessory[]): string => {
  let id = productId;

  // Add color index if specified
  if (colorIndex !== undefined && colorIndex >= 0) {
    id += `_c${colorIndex}`;
  }

  // Add accessory IDs if specified
  if (accessories && accessories.length > 0) {
    const accessoryIds = accessories.map(a => a._id).sort().join('-');
    id += `_${accessoryIds}`;
  }

  return id;
};

// Check if two accessory arrays are the same
const accessoriesMatch = (a?: Accessory[], b?: Accessory[]): boolean => {
  if (!a && !b) return true;
  if (!a || !b) return false;
  if (a.length !== b.length) return false;
  const aIds = a.map(acc => acc._id).sort();
  const bIds = b.map(acc => acc._id).sort();
  return aIds.every((id, i) => id === bIds[i]);
};

interface AppliedCoupon {
  code: string;
  type: string;
  value: number;
  discountAmount: number;
}

interface CartState {
  items: CartItem[];
  total: number;
  itemCount: number;
  appliedCoupon: AppliedCoupon | null;
}

type CartAction =
  | { type: 'ADD_ITEM'; payload: { product: Product; quantity: number; selectedColorIndex?: number; accessories?: Accessory[] } }
  | { type: 'REMOVE_ITEM'; payload: string } // Now uses cartItemId
  | { type: 'UPDATE_QUANTITY'; payload: { cartItemId: string; quantity: number } }
  | { type: 'CLEAR_CART' }
  | { type: 'LOAD_CART'; payload: CartItem[] }
  | { type: 'APPLY_COUPON'; payload: AppliedCoupon }
  | { type: 'REMOVE_COUPON' };

const initialState: CartState = {
  items: [],
  total: 0,
  itemCount: 0,
  appliedCoupon: null
};

const calculateCartTotals = (items: CartItem[]) => {
  const total = items.reduce((sum, item) => {
    let itemTotal = getEffectivePrice(item.product) * item.quantity;
    // Add accessories prices
    if (item.accessories && item.accessories.length > 0) {
      const accessoriesTotal = item.accessories.reduce((acc, accessory) => acc + accessory.price, 0);
      itemTotal += accessoriesTotal * item.quantity;
    }
    return sum + itemTotal;
  }, 0);
  const itemCount = items.reduce((count, item) => count + item.quantity, 0);
  return { total, itemCount };
};

const cartReducer = (state: CartState, action: CartAction): CartState => {
  switch (action.type) {
    case 'ADD_ITEM': {
      const { product, quantity, selectedColorIndex, accessories } = action.payload;
      const newCartItemId = generateCartItemId(product._id, selectedColorIndex, accessories);

      // Find existing item with SAME product AND SAME color AND SAME accessories
      const existingItem = state.items.find(item =>
        item.productId === product._id &&
        item.selectedColorIndex === selectedColorIndex &&
        accessoriesMatch(item.accessories, accessories)
      );

      let newItems: CartItem[];
      if (existingItem) {
        // Same product with same color and accessories - just update quantity
        newItems = state.items.map(item =>
          item.cartItemId === existingItem.cartItemId
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      } else {
        // New item (different product OR same product with different color/accessories)
        newItems = [...state.items, {
          cartItemId: newCartItemId,
          productId: product._id,
          product: product,
          quantity: quantity,
          selectedColorIndex: selectedColorIndex,
          accessories: accessories
        }];
      }

      const { total, itemCount } = calculateCartTotals(newItems);
      return { ...state, items: newItems, total, itemCount };
    }

    case 'REMOVE_ITEM': {
      // action.payload is now cartItemId
      const newItems = state.items.filter(item => item.cartItemId !== action.payload);
      const { total, itemCount } = calculateCartTotals(newItems);
      return { ...state, items: newItems, total, itemCount };
    }

    case 'UPDATE_QUANTITY': {
      const newItems = state.items.map(item =>
        item.cartItemId === action.payload.cartItemId
          ? { ...item, quantity: action.payload.quantity }
          : item
      ).filter(item => item.quantity > 0);

      const { total, itemCount } = calculateCartTotals(newItems);
      return { ...state, items: newItems, total, itemCount };
    }

    case 'APPLY_COUPON':
      return { ...state, appliedCoupon: action.payload };

    case 'REMOVE_COUPON':
      return { ...state, appliedCoupon: null };

    case 'CLEAR_CART':
      return { items: [], total: 0, itemCount: 0, appliedCoupon: null };

    case 'LOAD_CART': {
      // Migrate old cart items that don't have cartItemId
      const migratedItems = action.payload.map(item => ({
        ...item,
        cartItemId: item.cartItemId || generateCartItemId(item.productId, item.selectedColorIndex, item.accessories)
      }));
      const { total, itemCount } = calculateCartTotals(migratedItems);
      return { ...state, items: migratedItems, total, itemCount };
    }

    default:
      return state;
  }
};

interface CartContextType {
  state: CartState;
  addItem: (product: Product, quantity: number, selectedColorIndex?: number, accessories?: Accessory[]) => void;
  removeItem: (cartItemId: string) => void;
  updateQuantity: (cartItemId: string, quantity: number) => void;
  clearCart: () => void;
  clearCartForOrder: () => void;
  appliedCoupon: AppliedCoupon | null;
  applyCouponToCart: (coupon: AppliedCoupon) => void;
  removeCouponFromCart: () => void;
  showCartDrawer: () => void;
  hideCartDrawer: () => void;
  isCartDrawerOpen: boolean;
  showMobileNotification: () => void;
  hideMobileNotification: () => void;
  isMobileNotificationVisible: boolean;
  showMobileCartDrawer: () => void;
  hideMobileCartDrawer: () => void;
  isMobileCartDrawerOpen: boolean;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

interface CartProviderProps {
  children: ReactNode;
}

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState);
  const [isCartDrawerOpen, setIsCartDrawerOpen] = useState(false);
  const [isMobileNotificationVisible, setIsMobileNotificationVisible] = useState(false);
  const [isMobileCartDrawerOpen, setIsMobileCartDrawerOpen] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const savedCart = localStorage.getItem('cart');

    if (savedCart) {
      try {
        const parsedCart = JSON.parse(savedCart);
        if (Array.isArray(parsedCart) && parsedCart.length > 0) {
          dispatch({ type: 'LOAD_CART', payload: parsedCart });
        }
      } catch {
        // Clear corrupted cart data
        localStorage.removeItem('cart');
      }
    }

    const savedCoupon = localStorage.getItem('appliedCoupon');
    if (savedCoupon) {
      try {
        dispatch({ type: 'APPLY_COUPON', payload: JSON.parse(savedCoupon) });
      } catch {
        localStorage.removeItem('appliedCoupon');
      }
    }

    // Mark as initialized after loading attempt
    setIsInitialized(true);
  }, []);

  useEffect(() => {
    // Only save to localStorage after initialization to prevent race condition
    if (!isInitialized) {
      return;
    }

    if (state.items.length > 0) {
      localStorage.setItem('cart', JSON.stringify(state.items));
    } else {
      localStorage.removeItem('cart');
    }
  }, [state.items, isInitialized]);

  // Auto-hide mobile notification after 3 seconds with proper cleanup
  useEffect(() => {
    let timeoutId: NodeJS.Timeout | undefined;
    if (isMobileNotificationVisible) {
      timeoutId = setTimeout(() => {
        setIsMobileNotificationVisible(false);
      }, 3000);
    }
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isMobileNotificationVisible]);

  const addItem = useCallback((product: Product, quantity: number, selectedColorIndex?: number, accessories?: Accessory[]) => {
    dispatch({ type: 'ADD_ITEM', payload: { product, quantity, selectedColorIndex, accessories } });

    // Meta Pixel — AddToCart
    try {
      const productId = String((product as any)._id || (product as any).id || '');
      const accessoriesTotal = (accessories || []).reduce((s, a: any) => s + (a.price || 0), 0);
      const value = (getEffectivePrice(product) + accessoriesTotal) * quantity;
      trackAddToCart({
        content_ids: [productId],
        content_name: product.name,
        value,
        currency: 'TRY',
        contents: [{ id: productId, quantity }],
      });
    } catch {}

    // Show cart drawer on desktop, mobile notification on mobile
    const isMobile = window.innerWidth <= 768;

    if (isMobile) {
      setIsMobileNotificationVisible(true);
    } else {
      setIsCartDrawerOpen(true);
    }
  }, []);

  const removeItem = useCallback((cartItemId: string) => {
    dispatch({ type: 'REMOVE_ITEM', payload: cartItemId });
  }, []);

  const updateQuantity = useCallback((cartItemId: string, quantity: number) => {
    dispatch({ type: 'UPDATE_QUANTITY', payload: { cartItemId, quantity } });
  }, []);

  const clearCart = useCallback(() => {
    dispatch({ type: 'CLEAR_CART' });
    localStorage.removeItem('appliedCoupon');
  }, []);

  const clearCartForOrder = useCallback(() => {
    dispatch({ type: 'CLEAR_CART' });
    localStorage.removeItem('cart');
    localStorage.removeItem('appliedCoupon');
  }, []);

  const applyCouponToCart = useCallback((coupon: AppliedCoupon) => {
    dispatch({ type: 'APPLY_COUPON', payload: coupon });
    localStorage.setItem('appliedCoupon', JSON.stringify(coupon));
  }, []);

  const removeCouponFromCart = useCallback(() => {
    dispatch({ type: 'REMOVE_COUPON' });
    localStorage.removeItem('appliedCoupon');
  }, []);

  const showCartDrawer = useCallback(() => {
    setIsCartDrawerOpen(true);
  }, []);

  const hideCartDrawer = useCallback(() => {
    setIsCartDrawerOpen(false);
  }, []);

  const showMobileNotification = useCallback(() => {
    setIsMobileNotificationVisible(true);
  }, []);

  const hideMobileNotification = useCallback(() => {
    setIsMobileNotificationVisible(false);
  }, []);

  const showMobileCartDrawer = useCallback(() => {
    setIsMobileCartDrawerOpen(true);
  }, []);

  const hideMobileCartDrawer = useCallback(() => {
    setIsMobileCartDrawerOpen(false);
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    state,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    clearCartForOrder,
    appliedCoupon: state.appliedCoupon,
    applyCouponToCart,
    removeCouponFromCart,
    showCartDrawer,
    hideCartDrawer,
    isCartDrawerOpen,
    showMobileNotification,
    hideMobileNotification,
    isMobileNotificationVisible,
    showMobileCartDrawer,
    hideMobileCartDrawer,
    isMobileCartDrawerOpen
  }), [
    state,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    clearCartForOrder,
    applyCouponToCart,
    removeCouponFromCart,
    showCartDrawer,
    hideCartDrawer,
    isCartDrawerOpen,
    showMobileNotification,
    hideMobileNotification,
    isMobileNotificationVisible,
    showMobileCartDrawer,
    hideMobileCartDrawer,
    isMobileCartDrawerOpen
  ]);

  return (
    <CartContext.Provider value={contextValue}>
      {children}
    </CartContext.Provider>
  );
};